import { forwardRef, useId, type InputHTMLAttributes } from 'react';

import { cn } from '@/utils';

import { Field, fieldStyles, getFieldIds } from '../Field';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  label: string;
  hint?: string;
  error?: string;
  /** Supply to control the id yourself; otherwise one is generated. */
  id?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, hint, error, id, className, required, type = 'text', ...props },
  ref,
) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const { describedBy } = getFieldIds(inputId, hint, error);

  return (
    <Field id={inputId} label={label} hint={hint} error={error} required={required}>
      <input
        ref={ref}
        id={inputId}
        type={type}
        className={cn(fieldStyles.control, error && fieldStyles.controlInvalid, className)}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        required={required}
        {...props}
      />
    </Field>
  );
});
